import { memo } from "react";

import {
  type MoneyV2,
  type Maybe,
} from "@shopify/hydrogen-react/storefront-api-types";

import { Badge, type BadgeType } from "@components/Badge";
import { Heading } from "@components/Heading";
import { Price } from "@components/Price";
import {
  type CompareAtPriceDisplay,
  PriceContext,
} from "@components/Price/constants";
import { Text } from "@components/Text";
import { ProductHeroRating } from "@react/sections/ProductHero/ProductHeroRating";
import { tw } from "classnames";

type Props = {
  badgeText?: Maybe<string>;
  badgeType?: BadgeType;
  bundleValueText?: string;
  compareAtPrice: Maybe<MoneyV2> | undefined;
  compareAtPriceDisplay?: Maybe<CompareAtPriceDisplay>;
  description: Maybe<string>;
  price: Partial<MoneyV2>;
  reviewProducts?: { id: string; name: string }[];
  reviewsSectionId?: string;
  setSize?: Maybe<string>;
  showReviews?: boolean;
  title: Maybe<string>;
};

const styles = {
  titleContainer: tw(
    "flex",
    "gap-x-2",
    "items-baseline",
    "flex-wrap",
    "text-primary",
    "mg:mb-1"
  ),
  description: tw("mb-1", "text-secondary", "mg:mb-2.5"),
  price: tw("flex", "text-primary"),
  priceElement: tw("items-baseline"),
  pricingArea: tw("flex", "items-center"),
  badge: tw("ml-4"),
  compareAtPriceAsterisk: tw("text-secondary"),
};

const ProductIntroViewBase = ({
  badgeText,
  badgeType,
  bundleValueText,
  compareAtPrice,
  compareAtPriceDisplay,
  description,
  price,
  reviewProducts,
  reviewsSectionId,
  setSize,
  showReviews,
  title,
}: Props) => {
  return (
    <div>
      <div className={styles.titleContainer}>
        {title && (
          <>
            <Heading as="h1" size={{ sm: "sm", md: "md" }}>
              <span
                dangerouslySetInnerHTML={{
                  __html: title,
                }}
              ></span>
            </Heading>
            {setSize && (
              <Text as="body0" leading="loose">
                ({setSize})
              </Text>
            )}
          </>
        )}
      </div>
      <Text as="body1" className={styles.description}>
        {description}
      </Text>
      {showReviews && (
        <ProductHeroRating
          products={reviewProducts}
          sectionIdToLink={reviewsSectionId}
        />
      )}
      <div className={styles.pricingArea}>
        <div className={styles.price}>
          <Price
            bundleValueText={bundleValueText}
            className={styles.priceElement}
            compareAtPrice={compareAtPrice}
            compareAtPriceDisplay={compareAtPriceDisplay}
            context={PriceContext.PRODUCT_INTRO}
            price={price}
            showCurrencySuffix={false}
            size={{ md: "lg" }}
          />
        </div>
        {badgeText && (
          <Badge className={styles.badge} size="sm" type={badgeType}>
            {badgeText}
          </Badge>
        )}
      </div>
    </div>
  );
};

export const ProductIntroView = memo(ProductIntroViewBase);
